import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Returns the current date/time adjusted to Brazil timezone (America/Sao_Paulo).
 */
export function nowBR(): Date {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
}

/**
 * Returns today's date string in YYYY-MM-DD format, in Brazil timezone.
 */
export function todayBR(): string {
  const d = nowBR();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Dia de Brasília (YYYY-MM-DD) deslocado em `dias` a partir de hoje. A soma é
 * feita em UTC ao meio-dia, para não depender do fuso do navegador.
 */
export function diaBRDeslocado(dias: number): string {
  const d = new Date(`${todayBR()}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
}

/**
 * Returns current year in Brazil timezone.
 */
export function yearBR(): number {
  return nowBR().getFullYear();
}

/**
 * Returns current trimester (1-4) in Brazil timezone.
 */
export function trimestreBR(): number {
  return Math.ceil((nowBR().getMonth() + 1) / 3);
}

/**
 * Returns current month (1-12) in Brazil timezone.
 */
export function mesBR(): number {
  return nowBR().getMonth() + 1;
}

/**
 * Verifica se uma string YYYY-MM-DD (ou ISO YYYY-MM-DD HH:mm:ss) cai em dia útil (seg-sex).
 * Faz parse manual para evitar problemas de timezone.
 */
export function isWeekdayBR(dateStr?: string | null): boolean {
  if (!dateStr) return false;
  const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return false;
  const [, y, mo, d] = m;
  const dow = new Date(Number(y), Number(mo) - 1, Number(d)).getDay();
  return dow >= 1 && dow <= 5;
}

/**
 * Janela útil de jornada (07h–19h = 12h = 43200s).
 */
export const JORNADA_INICIO_H = 7;
export const JORNADA_FIM_H = 19;
export const JORNADA_MAX_SEC = (JORNADA_FIM_H - JORNADA_INICIO_H) * 3600;

/**
 * Calcula segundos do online_time que caem dentro da janela 07h–19h, dado
 * `first_time_day` e `last_time_day` (formato "YYYY-MM-DD HH:mm:ss" local).
 * Se faltar timestamp ou for inválido, retorna `min(rawSec, 43200)`.
 */
export function clampOnlineToJornada(
  rawSec: number,
  firstTs?: string | null,
  lastTs?: string | null
): number {
  const raw = Math.max(0, Number(rawSec) || 0);
  if (raw === 0) return 0;
  const parse = (s?: string | null) => {
    if (!s) return null;
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
    if (!m) return null;
    const [, y, mo, d, h, mi, se] = m;
    return { y: +y, mo: +mo, d: +d, h: +h, mi: +mi, se: +se };
  };
  const a = parse(firstTs);
  const b = parse(lastTs);
  if (!a || !b) return Math.min(raw, JORNADA_MAX_SEC);
  // segundos desde 00:00 do dia
  const aSec = a.h * 3600 + a.mi * 60 + a.se;
  const bSec = b.h * 3600 + b.mi * 60 + b.se;
  const ini = JORNADA_INICIO_H * 3600;
  const fim = JORNADA_FIM_H * 3600;
  const overlap = Math.max(0, Math.min(bSec, fim) - Math.max(aSec, ini));
  // Usa o menor entre o online bruto e a sobreposição com 07-19
  return Math.min(raw, overlap, JORNADA_MAX_SEC);
}

/**
 * Decodes HTML entities and ICS escape sequences from calendar text.
 */
export function decodeCalendarText(text: string): string {
  if (!text) return text;
  return text
    .replace(/\\n/g, '\n')
    .replace(/\\,/g, ',')
    .replace(/\\;/g, ';')
    .replace(/\\:/g, ':')
    .replace(/\\\\/g, '\\')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(parseInt(n)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, h) => String.fromCharCode(parseInt(h, 16)));
}
